import {
  Alert,
  Button,
  Card,
  Input,
  Popconfirm,
  Space,
  Tag,
  Typography,
  notification,
} from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";
import { useBank } from "./BankContext";

const { Text } = Typography;

/** Raw JSON editor for the quiz config doc (PUT /v4/quiz-config/).
 * The PUT is a MERGE: only keys present in the JSON are updated, null
 * values are ignored server-side, and deleting a key here does NOT
 * delete it from the stored doc. Server-side pydantic validation
 * (QuizConfigUpdate) still applies — bad values 400 with the reason. */

// Server-managed fields — stripped before PUT (harmless if sent, but
// hiding them from the diff keeps the editor honest about what saves).
const READ_ONLY = ["_id", "config_key", "version", "created_at", "updated_at"];

// Whitelist mirror of setu QuizConfigUpdate — unknown keys are silently
// dropped by the server, so warn about them before saving.
const EDITABLE = [
  "status", "max_level", "hearts_free_per_level", "hearts_cap",
  "hearts_max_per_question", "ad_gate_every", "session_ttl_minutes",
  "min_answer_interval_ms", "lifeline_earn_min_interval_s",
  "daily_coin_cap", "daily_attempts_per_level", "daily_level_reset",
  "min_seconds_per_question", "min_seconds_per_ad", "progression",
  "level_overrides", "info_points", "info_points_hi",
  "integrity_mode", "integrity_min_app_version",
  "a11y_mode", "a11y_extra_whitelist", "secure_screen",
];

const editable = (doc) => {
  const out = {};
  Object.keys(doc || {})
    .filter((k) => !READ_ONLY.includes(k))
    .sort()
    .forEach((k) => (out[k] = doc[k]));
  return out;
};

const JsonTab = () => {
  const { token, apiUrl } = useContext(Context);
  const { configKey } = useBank();

  const [stored, setStored] = useState(null); // full doc from GET
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    Fetch(`${apiUrl}/v4/quiz-config/?configKey=${configKey}`, token, (result) => {
      if (result && result.config) {
        setStored(result.config);
        setText(JSON.stringify(editable(result.config), null, 2));
      }
    });
  }, [apiUrl, token, counter, configKey]);

  const parsed = useMemo(() => {
    try {
      const value = JSON.parse(text);
      if (!value || Array.isArray(value) || typeof value !== "object")
        return { error: "top level must be a JSON object" };
      return { value };
    } catch (e) {
      return { error: e.message };
    }
  }, [text]);

  if (!stored) return null;

  const unknownKeys = parsed.value
    ? Object.keys(parsed.value).filter(
        (k) => !EDITABLE.includes(k) && !READ_ONLY.includes(k)
      )
    : [];

  const dirty =
    parsed.value &&
    JSON.stringify(parsed.value) !== JSON.stringify(editable(stored));

  const save = () => {
    const body = { ...parsed.value };
    READ_ONLY.forEach((k) => delete body[k]);
    setSaving(true);
    Put(
      `${apiUrl}/v4/quiz-config/?configKey=${configKey}`,
      token,
      body,
      (result) => {
        setSaving(false);
        if (result && result.success) {
          notification.success({
            message: `Config saved (version ${result.config?.version})`,
          });
          setCounter((c) => c + 1);
        } else {
          notification.error({
            message: "Save failed",
            description:
              typeof result?.detail === "string"
                ? result.detail
                : JSON.stringify(result?.detail || result),
          });
        }
      },
      () => {
        setSaving(false);
        notification.error({ message: "Save failed — network/server error" });
      }
    );
  };

  return (
    <Card
      title={
        <Space>
          Raw config JSON
          <Tag>{configKey}</Tag>
          <Tag color="geekblue">version {stored.version ?? "?"}</Tag>
          {stored.integrity_mode && (
            <Tag color={stored.integrity_mode === "off" ? "default" : "red"}>
              integrity: {stored.integrity_mode}
            </Tag>
          )}
          {stored.a11y_mode && (
            <Tag color={stored.a11y_mode === "off" ? "default" : "orange"}>
              a11y: {stored.a11y_mode}
            </Tag>
          )}
          {stored.secure_screen && <Tag color="purple">secure screen</Tag>}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => setCounter((c) => c + 1)}>
            Reload
          </Button>
          <Popconfirm
            title={`Save config "${configKey}"?`}
            description="Merged over the stored doc; server validates each field."
            onConfirm={save}
            disabled={!dirty || !!parsed.error}
          >
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty || !!parsed.error}
            >
              Save
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="PUT is a merge — only keys present here update; removing a key does NOT delete it. integrity_mode: off | dry_run | enforce | strict. a11y_mode (auto-clicker gate): off | dry_run | enforce."
      />
      {parsed.error && (
        <Alert style={{ marginBottom: 12 }} type="error" showIcon message={`Invalid JSON: ${parsed.error}`} />
      )}
      {unknownKeys.length > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message={`Unknown keys (server will ignore): ${unknownKeys.join(", ")}`}
        />
      )}
      <Input.TextArea
        value={text}
        onChange={(e) => setText(e.target.value)}
        autoSize={{ minRows: 24, maxRows: 40 }}
        style={{ fontFamily: "monospace", fontSize: 13 }}
        spellCheck={false}
      />
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          Editable fields: {EDITABLE.join(", ")}
        </Text>
      </div>
    </Card>
  );
};

export default JsonTab;
